import React, { useEffect, useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Dumbbell, Loader2, Target, ChevronRight, Flame } from 'lucide-react';
import { useClientAuth } from '../../context/ClientAuthContext';

const CARD = 'relative overflow-hidden rounded-2xl bg-zinc-900/50 backdrop-blur-xl border border-white/10 shadow-[0_0_30px_-12px_rgba(217,70,239,0.22)]';

/** Aperçu compact des programmes "Programs Pro" — clique pour aller les exécuter dans l'onglet Entraînements. */
export const CoachingWorkout = ({ en, onOpenProgram }) => {
  const { authFetch } = useClientAuth();
  const api = useCallback((p) => authFetch(p).then((r) => (r.ok ? r.json() : Promise.reject(r))), [authFetch]);
  const [data, setData] = useState(null);

  useEffect(() => {
    api('/api/tracking/programs-pro').then(setData).catch(() => setData({ programs: [] }));
  }, [api]);

  if (!data) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;

  const programs = data.programs || [];

  return (
    <div data-testid="workout-program-table">
      <div className="flex items-center gap-2.5 mb-5">
        <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-purple-500 to-primary flex items-center justify-center shrink-0">
          <Dumbbell className="w-4.5 h-4.5 text-white" />
        </div>
        <div>
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">{en ? 'Custom training' : 'Musculation perso'}</p>
          <h3 className="font-bold text-white text-lg leading-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>{en ? 'My training programs' : 'Mes programmes'}</h3>
        </div>
      </div>

      {programs.length === 0 ? (
        <div className={`${CARD} p-10 text-center`} data-testid="programs-empty">
          <Dumbbell className="w-10 h-10 mx-auto mb-3 text-zinc-700" />
          <p className="text-zinc-400 text-sm">{en ? 'Coach Laurent is building your program. Stay tuned!' : 'Le coach Laurent prépare votre programme. À très vite !'}</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-4">
          {programs.map((p, pi) => {
            const pct = p.total_target ? Math.min(100, Math.round((p.total_done / p.total_target) * 100)) : 0;
            return (
              <motion.button
                key={p.program_id}
                onClick={() => onOpenProgram && onOpenProgram(p.program_id)}
                initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: pi * 0.05 }}
                className={`${CARD} p-5 text-left hover:border-primary/40 transition-colors group`}
                data-testid="coaching-program-card"
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h4 className="font-bold text-white flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                      <span className="w-1.5 h-5 rounded-full bg-gradient-to-b from-purple-400 to-primary" /> {p.name}
                    </h4>
                    <p className="text-xs text-zinc-400 mt-1.5 flex items-center gap-1.5"><Target className="w-3 h-3" /> {p.goal}</p>
                  </div>
                  <ChevronRight className="w-5 h-5 text-zinc-600 group-hover:text-primary group-hover:translate-x-1 transition-all shrink-0" />
                </div>
                <div className="mt-4">
                  <div className="flex items-center justify-between text-[11px] mb-1.5">
                    <span className="text-zinc-500 flex items-center gap-1"><Flame className="w-3 h-3 text-orange-400" /> {en ? 'Progress' : 'Progression'}</span>
                    <span className="font-mono font-bold text-white">{p.total_done}/{p.total_target} {en ? 'sessions' : 'séances'}</span>
                  </div>
                  <div className="h-2 rounded-full bg-white/8 overflow-hidden">
                    <motion.div initial={{ width: 0 }} animate={{ width: `${pct}%` }} transition={{ duration: 0.8 }} className="h-full rounded-full bg-gradient-to-r from-purple-500 to-primary" />
                  </div>
                </div>
              </motion.button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default CoachingWorkout;
